import { X, AlertTriangle, Loader2 } from 'lucide-react';
import { Portal } from '../Portal';

interface DeleteConfirmationDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  onClose?: () => void;
  isLoading?: boolean;
}


export function DeleteConfirmationDialog({
  isOpen,
  title,
  message,
  confirmText = 'Delete',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  onClose,
  isLoading = false
}: DeleteConfirmationDialogProps) {
  if (!isOpen) return null;

  return (
    <Portal>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
          onClick={onClose || onCancel}
        />

        {/* Dialog Panel */}
        <div className="relative bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-2xl max-w-sm w-full border border-gray-100 dark:border-gray-800 overflow-hidden transform transition-all scale-100">

          <div className="flex flex-col items-center text-center p-6 space-y-4">
            {/* Icon */}
            <div className="w-14 h-14 bg-red-500/10 dark:bg-red-500/20 rounded-full flex items-center justify-center ring-4 ring-red-500/5 dark:ring-red-500/10">
              <AlertTriangle className="w-7 h-7 text-red-600 dark:text-red-500" />
            </div>

            {/* Content */}
            <div className="space-y-2">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">{title}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-300 leading-relaxed max-w-[280px] mx-auto">
                {message}
              </p>
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="grid grid-cols-2 gap-3 p-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-100 dark:border-gray-800">
            <button
              onClick={onCancel}
              disabled={isLoading}
              className="px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors focus:ring-2 focus:ring-gray-200 dark:focus:ring-gray-700 outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="px-4 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-all shadow-lg shadow-red-500/20 hover:shadow-red-500/30 focus:ring-2 focus:ring-red-500/50 outline-none disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-red-600"
            >
              {isLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Deleting...</span>
                </div>
              ) : (
                confirmText
              )}
            </button>
          </div>

          {/* Close Button (Absolute) */}
          {onClose && (
            <button
              onClick={onClose}
              className="absolute top-3 right-3 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <X size={18} />
            </button>
          )}
        </div>
      </div>
    </Portal>
  );
}